<template>
  <div
    class="chatbot">
    <div
      id="chatbot-wrapper"
      class="chatbot-wrapper">
      <transition-group
        ref="content"
        name="chatbot-transition"
        enter-active-class="animate__animated animate__fadeIn animate__fast"
        class="chatbot-content"
        tag="div">
        <Bubble
          v-for="bubble in bubbles"
          :key="bubble.key"
          :data="bubble.content"
          :delay="bubble.delay"
          :is-editable="bubble.isEditable"
          :is-large="isLarge"
          :is-user="bubble.isUser"
          :user="bubble.user"
          class="chatbot-bubble"
          @ready="handleBubbleReady(bubble)"
          @error="handleBubbleError(bubble)"
          @select="handleBubbleSelected(bubble)" />
      </transition-group>

      <div
        class="chatbot-controls">
        <transition-group
          name="chatbot-action-transition"
          enter-active-class="animate__animated animate__fadeIn animate__faster"
          leave-active-class="animate__animated animate__fadeOut animate__faster">
          <component
            :is="actionsMap[action.name]"
            v-for="(action, index) in actions"
            :key="index"
            :data="action.data"
            :is-large="isLarge"
            @validate="handleValidationClick(action, $event)" />
        </transition-group>
      </div>
    </div>

    <MessageInput
      :is-disabled="!chatMessageAction"
      @send-message="handleValidationClick(chatMessageAction, $event)" />
  </div>
</template>

<script>
import AudioService from "@/services/AudioService"
import Bubble from "@/components/chatbot/Bubbles/Bubble"
import ButtonAction from "@/components/chatbot/Bubbles/actions/ButtonAction"
import CalendarAction from "@/components/chatbot/Bubbles/actions/CalendarAction"
import CategoriesCheckboxesAction from "@/components/chatbot/Bubbles/actions/CategoriesCheckboxesAction"
import CheckboxesAction from "@/components/chatbot/Bubbles/actions/CheckboxesAction"
import config from "@/app.config"
import InputAction from "@/components/chatbot/Bubbles/actions/InputAction"
import MessageInput from "@/components/chatbot/MessageInput"
import msgIncomingSound from "@/assets/sounds/msg_incoming.mp3"
import msgSentSound from "@/assets/sounds/msg_sent.mp3"
import RadiosAction from "@/components/chatbot/Bubbles/actions/RadiosAction"

export default {
  name: "Chatbot",
  components: {
    Bubble,
    MessageInput
  },
  props: {
    flow: {
      type: Object,
      required: true
    },
    isLarge: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      idleTimeout: null,
      isChatbotDebug: config.chatbotDebug === "true",
      bubbles: [],
      actions: [],
      actionsMap: {
        button: ButtonAction,
        calendar: CalendarAction,
        checkboxes: CheckboxesAction,
        categories: CategoriesCheckboxesAction,
        radios: RadiosAction,
        input: InputAction
      }
    }
  },
  computed: {
    flowSteps() {
      return this.flow.chat.reduce((obj, item) => ({
        ...obj,
        [item.id]: item
      }), {})
    },
    chatMessageAction() {
      return this.actions.find(action => action.name === "message")
    }
  },
  mounted() {
    this.goTo(this.flow.entry)
  },
  updated() {
    setTimeout(() => {
      this.scrollToBottom()
    }, 0)
  },
  methods: {
    goTo(stepId) {
      let step = this.flowSteps[stepId]
      if (!step) {
        return
      }

      this.bubbles.push({
        id: step.id,
        content: step.asyncContent,
        key: step.id + "-" + Math.floor(Math.random() * 10000),
        isUser: false,
        delay: this.isChatbotDebug ? 0 : step.delay,
        isEditable: false
      })
    },
    handleBubbleReady(bubble) {
      let step = this.flowSteps[bubble.id]
      if (!step) {
        return
      }

      if (step.conversationId) {
        this.joinSocketRoom(step.conversationId)
      }

      if (step.callback) {
        step.callback()
      }

      if (step.next) {
        setTimeout(() => {
          this.goTo(step.next)
        }, this.isChatbotDebug ? 0 : 700)
        return
      }

      this.actions = step.actions
    },
    handleBubbleError() {
      this.$emit("finished")
    },
    handleValidationClick(action, value) {
      let bubbleId = Math.floor(Math.random() * 10000)
      this.bubbles.push({
        id: bubbleId,
        content: action.getResponseContent(value),
        key: bubbleId,
        isUser: true,
        delay: 0,
        isEditable: false
      })

      if (action.callback) {
        action.callback(value)
      }

      if (action.next) {
        this.actions = []
        setTimeout(() => {
          this.goTo(action.next)
        }, this.isChatbotDebug ? 0 : 900)
        return
      }
    },
    restart() {
      this.bubbles = []
      this.actions = []
      this.goTo(this.flow.entry)
    },
    scrollToBottom() {
      let element = document.getElementById("chatbot-wrapper")
      element.scrollTop = element.scrollHeight
    },
    joinSocketRoom(conversationId) {
      let channel = this.$pusher.subscribe(`private-conversations.${conversationId}`)

      channel.bind("new-message", data => {
        if (data.writer_type === "Customer") {
          AudioService.emitSound(msgSentSound)
          return
        }

        clearTimeout(this.idleTimeout)
        this.idleTimeout = null

        let newRepresentativeUser = null
        let representativeUserId = this.$store.getters["representativeUserId"]
        if (data.writer.id !== representativeUserId) {
          newRepresentativeUser = data.writer
          this.$store.commit("setRepresentativeUser", newRepresentativeUser)
        }

        this.notifyThoroughly("Nouveau message !")
        window.parent.postMessage({ instructionCode: "unread-message" }, "*")

        this.bubbles.push({
          id: data.id,
          content: data.content,
          key: data.id,
          isUser: false,
          delay: 0,
          isEditable: false,
          user: newRepresentativeUser
        })
      })

      if (this.idleTimeout) {
        clearTimeout(this.idleTimeout)
      }

      this.idleTimeout = setTimeout(() => {
        let bubbleId = Math.floor(Math.random() * 10000)
        this.bubbles.push({
          id: bubbleId,
          content: "Bonjour,\nMalheureusement aucun de nos conseillers n’est disponible.\nNous vous répondrons dès que possible.",
          key: bubbleId,
          isUser: false,
          delay: null,
          isEditable: false
        })
      }, 30000)
    },
    notifyThoroughly(newTitle) {
      // Make page title blink
      let originalTitle = document.title
      let newMessageTitleIndication = setInterval(() => {
        document.title = document.title === newTitle ? originalTitle : newTitle
      }, 1200)
      setTimeout(() => {
        clearInterval(newMessageTitleIndication)
        document.title = originalTitle
      }, 12000)

      // Make device vibrate
      window.navigator.vibrate(200)

      // Emit sound
      AudioService.emitSound(msgIncomingSound)
    },
  }
}
</script>

<style lang="scss" scoped>
.chatbot {
  display: flex;
  flex-direction: column;
  overflow: auto;

  .chatbot-wrapper {
    flex: 1;
    display: flex;
    flex-direction: column;
    overflow: auto;
    padding: 16px;

    .chatbot-content {
      width: 100%;
      display: flex;
      flex-direction: column;
      flex-shrink: 0;

      h1 {
        height: 0;
        margin-bottom: 128px;
        transition: margin-bottom 0.8s ease, opacity 0.6s ease;
      }

      .chatbot-bubble {
        flex: none;
        margin-bottom: 12px;
      }
    }

    .chatbot-controls {
      width: 100%;
      display: flex;
      flex: 1;
      flex-direction: column;
      justify-content: flex-end;
      padding-top: 16px;
      margin-left: auto;
      text-align: right;
      flex-shrink: 0;

      > span {
        display: flex;
        flex-direction: column;
        align-items: flex-end;

        > * {
          margin-bottom: 16px;

          &:last-child {
            margin-bottom: 0;
          }
        }
      }
    }

    &.fade-overlay {
      h1 {
        margin-bottom: 0;
        opacity: 0;
      }
    }
  }
}
</style>
